// src/Layouts/Dashboard/Sidebar.tsx
import React, { useState, useRef, useEffect, useCallback, JSX } from 'react';
import { useLocation } from 'react-router-dom';
import { NavigateFunction } from 'react-router-dom';
import { isBeta, showDevTools } from '../../config/env';
import { ruleOf } from '../../stateManagers/roleAccess/access';
import { useRoleAccess } from '../../stateManagers/roleAccess/useRoleAccess';
import './Sidebar.css';

interface SidebarProps {
  onNavigate: NavigateFunction;
  onLogout: () => void;
}

// ── Inline SVG icon wrapper ───────────────────────────────────────────────────
const Ico = ({
  d, size = 18, style = {}, fill = false,
}: {
  d: string | string[]; size?: number; style?: React.CSSProperties; fill?: boolean;
}) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24"
    fill={fill ? 'currentColor' : 'none'}
    stroke={fill ? 'none' : 'currentColor'}
    strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', ...style }}
  >
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);

// ── Icons ─────────────────────────────────────────────────────────────────────
const IcoHome        = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z" />;
const IcoGrad        = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M22 10v6M2 10l10-5 10 5-10 5z', 'M6 12v5c3 3 9 3 12 0v-5']} />;
const IcoUsers       = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2', 'M9 7a4 4 0 100 8 4 4 0 000-8z', 'M23 21v-2a4 4 0 00-3-3.87', 'M16 3.13a4 4 0 010 7.75']} />;
const IcoBell        = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9', 'M13.73 21a2 2 0 01-3.46 0']} />;
const IcoInfo        = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z', 'M12 8h.01M11 12h1v4h1']} />;
const IcoLock        = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M19 11H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2z', 'M7 11V7a5 5 0 0110 0v4']} />;
const IcoKey         = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />;
const IcoCalPlus     = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z', 'M12 14v6M9 17h6']} />;
const IcoCalStar     = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z', 'M12 17l-1.5 1 .5-1.7-1.4-1 1.7-.1L12 14l.7 1.2 1.7.1-1.4 1 .5 1.7z']} />;
const IcoClock       = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z', 'M12 6v6l4 2']} />;
const IcoFilePdf     = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z', 'M14 2v6h6', 'M9 15h6M9 11h2']} />;
const IcoTicket      = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M2 9a3 3 0 010 6v2a2 2 0 002 2h16a2 2 0 002-2v-2a3 3 0 010-6V7a2 2 0 00-2-2H4a2 2 0 00-2 2v2z', 'M13 5v2M13 17v2M13 11v2']} />;
const IcoMail        = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z', 'M22 6l-10 7L2 6']} />;
const IcoEditPencil  = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />;
const IcoFolder      = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />;
const IcoReceipt     = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1zM9 9h6M9 13h6" />;
const IcoCog         = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M12 15a3 3 0 100-6 3 3 0 000 6z', 'M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z']} />;
const IcoChevronDown = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M6 9l6 6 6-6" />;
const IcoSignOut     = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4', 'M16 17l5-5-5-5', 'M21 12H9']} />;
const IcoGrid        = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z" />;
const IcoSidebarOpen  = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M3 12h18', 'M3 6h18', 'M3 18h18']} />;
const IcoSidebarClose = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M19 12H5', 'M12 19l-7-7 7-7']} />;
const IcoImages = (p: { size?: number; style?: React.CSSProperties }) => (
  <Ico {...p} d={[
    'M21 19H3a2 2 0 01-2-2V7a2 2 0 012-2h3l2-3h8l2 3h3a2 2 0 012 2z',
    'M12 17a4 4 0 100-8 4 4 0 000 8z'
  ]} />
);
const IcoVisitor = (p: { size?: number; style?: React.CSSProperties }) => (
  <Ico {...p} d={[
    'M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2',
    'M9 7a4 4 0 100 8 4 4 0 000-8z',
    'M22 11h-6M19 8v6',
  ]} />
);
const IcoStaff = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M12 2a5 5 0 1 0 0 10 5 5 0 0 0 0-10z', 'M4 20c0-4 3.6-7 8-7s8 3 8 7', 'M17 8h5M19.5 5.5v5']} />;
const IcoSparkle = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z']} />;
const IcoIdCard = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M2 5a2 2 0 012-2h16a2 2 0 012 2v14a2 2 0 01-2 2H4a2 2 0 01-2-2V5z', 'M8 10a2 2 0 100-4 2 2 0 000 4z', 'M14 8h4M14 12h4M8 14h12']} />;
const IcoPayroll = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={[
  'M4 4a2 2 0 012-2h8l4 4v14a2 2 0 01-2 2H6a2 2 0 01-2-2V4z',
  'M12 2v4h4',
  'M9 10a2 2 0 100-4 2 2 0 000 4z',
  'M9 8v-.5M9 12v-.5',
  'M13 8h3M13 10h3',
  'M7 14h8M7 17h8',
]} />;
const IcoHistory = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={[
  'M3 12a9 9 0 109-9 9 9 0 00-9 9z',
  'M3 12h4M12 7v5l3 2',
]} />;

const IcoBook = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M4 19.5A2.5 2.5 0 016.5 17H20', 'M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z']} />;

const IcoWallet = (p: { size?: number; style?: React.CSSProperties }) => (
  <Ico {...p} d={['M21 12V7H5a2 2 0 010-4h14v4', 'M3 5v14a2 2 0 002 2h16v-5', 'M18 12a2 2 0 100 4 2 2 0 000-4z']} />
);

const IcoExpense = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={[
  'M3 6h18v12H3z',
  'M3 10h18',
  'M7 15h2',
  'M12 6v3M12 15l-2.5-2.5M12 15l2.5-2.5',
]} />;

const IcoFees = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={[
  'M3 8a9 3 0 0018 0 9 3 0 00-18 0z',
  'M3 8v4a9 3 0 0018 0V8',
  'M3 12v4a9 3 0 0018 0v-4',
]} />;

const IcoVisitorLog = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={[
  'M15 3H6a2 2 0 00-2 2v14a2 2 0 002 2h9',
  'M15 3v18',
  'M11 12h.01',
  'M18 8l4 4-4 4',
  'M22 12h-9',
]} />;

const IcoStudentIssue = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={[
  'M4 21V4a1 1 0 011-1h11.5l3.5 3.5V15a1 1 0 01-1 1H7l-3 3z',
  'M12 7v5',
  'M12 15h.01',
]} />;

// ── Additional distinct icons to remove duplicates ────────────────────────────
const IcoTable       = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M3 4h18v16H3z', 'M3 9h18', 'M3 15h18', 'M9 4v16', 'M15 4v16']} />;
const IcoLayers       = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M12 2l9 5-9 5-9-5 9-5z', 'M3 12l9 5 9-5', 'M3 17l9 5 9-5']} />;
const IcoUserCheck    = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2', 'M9 7a4 4 0 100 8 4 4 0 000-8z', 'M17 11l2 2 4-4']} />;
const IcoArchive      = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M21 8H3v13h18z', 'M1 3h22v5H1z', 'M10 12h4']} />;
const IcoUserGear     = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M13 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2', 'M7 7a4 4 0 100 8 4 4 0 000-8z', 'M18 14v1.6M18 20.4V22M14.6 16l1.4.8M20 19.2l1.4.8M14.6 20l1.4-.8M20 16.8l1.4-.8', 'M18 16a2 2 0 100 4 2 2 0 000-4z']} />;
const IcoScanCheck    = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M3 7V5a2 2 0 012-2h2', 'M17 3h2a2 2 0 012 2v2', 'M21 17v2a2 2 0 01-2 2h-2', 'M7 21H5a2 2 0 01-2-2v-2', 'M8 12l2.5 2.5L16 9']} />;
const IcoFileText     = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z', 'M14 2v6h6', 'M9 13h6M9 17h6M9 9h1']} />;
const IcoBadgeCheck   = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M12 2l2.2 1.3 2.6-.2 1.1 2.3 2.3 1.1-.2 2.6L22 12l-1.3 2.2.2 2.6-2.3 1.1-1.1 2.3-2.6-.2L12 22l-2.2-1.3-2.6.2-1.1-2.3-2.3-1.1.2-2.6L2 12l1.3-2.2-.2-2.6 2.3-1.1 1.1-2.3 2.6.2z', 'M9 12l2 2 4-4']} />;
const IcoBriefcaseClock = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M3 7h18v13H3z', 'M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2', 'M12 11v3l2 1']} />;
const IcoDoorOut = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d={['M13 3H6a2 2 0 00-2 2v14a2 2 0 002 2h7', 'M10 12h.01', 'M17 8l4 4-4 4', 'M21 12h-8']} />;

// ── Types ─────────────────────────────────────────────────────────────────────
type IconComponent = (p: { size?: number; style?: React.CSSProperties }) => JSX.Element;

interface FlatItem {
  type: 'item';
  path: string;
  Icon: IconComponent;
  label: string;
  isNew?: boolean;
  betaOnly?: boolean;
  devOnly?: boolean;
}

interface GroupItem {
  type: 'group';
  id: string;
  Icon: IconComponent;
  label: string;
  isNew?: boolean;
  betaOnly?: boolean;
  devOnly?: boolean;
  children: {
    path: string;
    Icon: IconComponent;
    label: string;
    betaOnly?: boolean;
    devOnly?: boolean;
  }[];
}

type MenuItem = FlatItem | GroupItem;

// ── Constants ─────────────────────────────────────────────────────────────────
const SB_BG       = 'linear-gradient(180deg, #1e3a8a 0%, #2855ae 55%, #3b82f6 100%)';
const COLLAPSED_W = 72;
const HIDE_DELAY  = 180;

// ── Menu config ───────────────────────────────────────────────────────────────
// Who sees each link is decided by ROUTE_ACCESS in stateManagers/roleAccess/access.ts
// (looked up by `path`). Nothing permission-related lives here.
const menuConfig: MenuItem[] = [
  { type: 'item', path: '/', Icon: IcoHome, label: 'Dashboard' },

  {
    type: 'group',
    id: 'students',
    Icon: IcoGrad,
    label: 'Students',
    children: [
      { path: '/students',             Icon: IcoGrad,      label: 'Students List' },
      { path: '/students/bulk-photos', Icon: IcoImages,    label: 'Bulk Profile Photos' },
      { path: '/students/attendance',  Icon: IcoScanCheck, label: 'Class Attendance' },
    ],
  },

  { type: 'item', path: '/teachers', Icon: IcoUsers, label: 'Teachers' },

  {
    type: 'group',
    id: 'staff',
    Icon: IcoStaff,
    label: 'Staff',
    children: [
      { path: '/staff',                  Icon: IcoUserGear,  label: 'Staff' },
      { path: '/staff/staff-attendance', Icon: IcoScanCheck, label: 'Staff Attendance' },
    ],
  },

  {
    type: 'group',
    id: 'timetable',
    Icon: IcoClock,
    label: 'Timetable',
    children: [
      { path: '/timetable',                   Icon: IcoTable,     label: 'Timetable' },
      { path: '/timetable/modern',            Icon: IcoSparkle,   label: 'Smart Timetable' },
      { path: '/timetable/assigned-teachers', Icon: IcoUserCheck, label: 'Assigned Teachers' },
    ],
  },

  { type: 'item', path: '/noticeboard', Icon: IcoBell,    label: 'Noticeboard' },
  { type: 'item', path: '/events',      Icon: IcoCalStar, label: 'Events' },

  {
    type: 'group',
    id: 'exams',
    Icon: IcoCalPlus,
    label: 'Exams',
    isNew: true,
    children: [
      { path: '/exams',            Icon: IcoCalPlus, label: 'Date Sheet' },
      { path: '/exams/award-role', Icon: IcoTicket,  label: 'Award Role' },
    ],
  },

  { type: 'item', path: '/student-issues', Icon: IcoInfo,       label: 'Student Issues' },
  { type: 'item', path: '/visitors',       Icon: IcoVisitorLog, label: 'Visitor Log' },

  {
    type: 'group',
    id: 'fees',
    Icon: IcoFees,
    label: 'Fees',
    children: [
      { path: '/fees',         Icon: IcoReceipt, label: 'Fees Panel' },
      { path: '/fees/history', Icon: IcoHistory, label: 'Payment History' },
    ],
  },

  {
    type: 'group',
    id: 'gate',
    Icon: IcoScanCheck,
    label: 'Gate',
    isNew: true,
    children: [
      { path: '/gate-entry',  Icon: IcoScanCheck, label: 'Gate Entry' },
      { path: '/gate-passes', Icon: IcoDoorOut,   label: 'Gate Passes' },
    ],
  },

  { type: 'item', path: '/expenses', Icon: IcoExpense, label: 'Expenses' },

  {
    type: 'group',
    id: 'payroll-management',
    Icon: IcoPayroll,
    label: 'Payroll',
    children: [
      { path: '/payroll-management/records', Icon: IcoBriefcaseClock, label: 'Payroll Records' },
      { path: '/payroll-management',         Icon: IcoIdCard,         label: 'Salary Setup' },
    ],
  },

  {
    type: 'group',
    id: 'letter-composer',
    Icon: IcoMail,
    label: 'Letter Composer',
    children: [
      { path: '/letterheads/new',      Icon: IcoEditPencil, label: 'Generate Letter' },
      { path: '/letterheads',          Icon: IcoArchive,    label: 'Previously Generated' },
      { path: '/letterhead-templates', Icon: IcoFilePdf,    label: 'Letter Templates' },
    ],
  },

  {
    type: 'group',
    id: 'credentials',
    Icon: IcoIdCard,
    label: 'Credentials',
    children: [
      { path: '/credentials/students', Icon: IcoLock,  label: 'Student Credentials' },
      { path: '/credentials/teachers', Icon: IcoUsers, label: 'Teacher Credentials' },
      { path: '/bulk-password-reset',  Icon: IcoKey,   label: 'Bulk Password Reset' },
    ],
  },

  {
    type: 'group',
    id: 'hall-tickets',
    Icon: IcoTicket,
    label: 'Hall Ticket',
    children: [
      { path: '/hall-tickets',           Icon: IcoTicket, label: 'Hall Ticket' },
      { path: '/hall-tickets/gate-pass', Icon: IcoIdCard, label: 'Gate Pass' },
    ],
  },

  {
    type: 'group',
    id: 'students-pdf',
    Icon: IcoLayers,
    label: 'Record Reports',
    children: [
      { path: '/students-pdf',                      Icon: IcoFileText,   label: 'Students PDF' },
      { path: '/students-pdf/UDISEAnomalyDetector', Icon: IcoBadgeCheck, label: 'Udise Pulse' },
    ],
  },

  { type: 'item', path: '/subjects', Icon: IcoBook, label: 'Subjects', devOnly: true },

  { type: 'item', path: '/settings', Icon: IcoCog, label: 'Settings' },
];

// ── Filter by environment ─────────────────────────────────────────────────────
// betaOnly items are stripped out entirely on production builds.
// devOnly items only show in local dev / beta (see showDevTools in config/env).
// Children with betaOnly/devOnly are also filtered per-group.
function filterMenuForEnv(items: MenuItem[]): MenuItem[] {
  return items
    .filter(item => !item.betaOnly || isBeta)
    .filter(item => !item.devOnly || showDevTools)
    .map(item => {
      if (item.type === 'group') {
        return {
          ...item,
          children: item.children
            .filter(c => !c.betaOnly || isBeta)
            .filter(c => !c.devOnly || showDevTools),
        };
      }
      return item;
    });
}

const visibleMenu = filterMenuForEnv(menuConfig);

// ── Active path helper ────────────────────────────────────────────────────────
const ALL_MENU_PATHS: string[] = visibleMenu.flatMap(item =>
  item.type === 'group'
    ? item.children.map(c => c.path)
    : [item.path],
);

function isActivePath(currentPath: string, itemPath: string): boolean {
  if (itemPath === '/') return currentPath === '/';
  if (currentPath === itemPath) return true;
  if (currentPath.startsWith(itemPath + '/')) {
    const moreSpecificEntryExists = ALL_MENU_PATHS.some(
      p => p !== itemPath && currentPath === p,
    );
    return !moreSpecificEntryExists;
  }
  return false;
}

// ── Hover-with-delay hook ─────────────────────────────────────────────────────
function useHoverPanel(hideDelay = HIDE_DELAY) {
  const [open, setOpen] = React.useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const enter = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    setOpen(true);
  }, []);
  const leave = useCallback(() => {
    timer.current = setTimeout(() => setOpen(false), hideDelay);
  }, [hideDelay]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  return { open, triggerProps: { onMouseEnter: enter, onMouseLeave: leave }, panelProps: { onMouseEnter: enter, onMouseLeave: leave } };
}

// ── Group renderer ────────────────────────────────────────────────────────────
interface GroupRendererProps {
  item: GroupItem;
  expanded: boolean;
  currentPath: string;
  onNavigate: NavigateFunction;
  setExpanded: (v: boolean) => void;
  updatePositions: () => void;
}

const GroupRenderer: React.FC<GroupRendererProps> = ({
  item, expanded, currentPath, onNavigate, setExpanded, updatePositions,
}) => {
  const [open, setOpen] = useState(true);
  const btnRef = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(0);
  const hover = useHoverPanel();

  const isActive = item.children.some(c => isActivePath(currentPath, c.path));

  const refreshTop = useCallback(() => {
    updatePositions();
    if (btnRef.current) setTop(btnRef.current.getBoundingClientRect().top);
  }, [updatePositions]);

  useEffect(() => { refreshTop(); }, [expanded, refreshTop]);

  if (expanded) {
    return (
      <div style={{ marginBottom: 4 }}>
        <button
          className={`sb-btn ${isActive ? (item.isNew ? 'new-active' : 'active') : ''}`}
          onClick={() => setOpen(p => !p)}
          style={{ justifyContent: 'space-between' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <item.Icon size={17} style={{ color: 'white', flexShrink: 0 }} />
            <span className="sb-label">{item.label}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {item.betaOnly && isBeta && <span className="sb-beta-badge">BETA</span>}
            {item.isNew && !item.betaOnly && <span className="sb-new-badge">NEW</span>}
            <IcoChevronDown
              size={14}
              style={{
                color: 'rgba(255,255,255,0.6)', flexShrink: 0,
                transition: 'transform 0.28s cubic-bezier(0.4,0,0.2,1)',
                transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
              }}
            />
          </div>
        </button>

        <div style={{
          marginTop: 2, marginLeft: 10,
          borderLeft: '2px solid rgba(255,255,255,0.16)', paddingLeft: 4,
          maxHeight: open ? '400px' : '0px',
          opacity: open ? 1 : 0,
          overflowY: open ? 'auto' : 'hidden',
          overflowX: 'hidden',
          scrollbarWidth: 'none',
          transition: 'max-height 0.35s cubic-bezier(0.4,0,0.2,1), opacity 0.25s ease',
        }}>
          {item.children.map(({ path, Icon, label }) => (
            <button
              key={path}
              className={`sb-sub-btn ${isActivePath(currentPath, path) ? 'active' : ''}`}
              onClick={() => onNavigate(path)}
            >
              <Icon size={15} style={{ flexShrink: 0, color: 'white' }} />
              <span className="sb-label">{label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── Collapsed mode ──────────────────────────────────────────────────────────
  return (
    <div style={{ marginBottom: 4 }}>
      <div
        ref={btnRef}
        {...hover.triggerProps}
        onMouseEnter={() => { refreshTop(); hover.triggerProps.onMouseEnter(); }}
      >
        <button
          className={`sb-btn ${isActive ? (item.isNew ? 'new-active' : 'active') : ''}`}
          style={{ justifyContent: 'center', padding: '11px', position: 'relative' }}
          onClick={() => { setExpanded(true); setOpen(true); }}
        >
          <item.Icon size={17} style={{ color: 'white', flexShrink: 0 }} />
          {(item.isNew || item.betaOnly) && (
            <span style={{
              position: 'absolute', top: 7, right: 7,
              width: 7, height: 7, borderRadius: '50%',
              background: item.betaOnly ? '#a78bfa' : '#fbbf24',
              border: '1.5px solid rgba(30,58,138,0.8)',
            }} />
          )}
        </button>
      </div>

      {hover.open && (
        <div className="sb-float-panel" style={{ top }} {...hover.panelProps}>
          <div className="sb-float-title">{item.label}</div>
          {item.children.map(({ path, Icon, label }) => (
            <button
              key={path}
              className={`sb-float-item ${isActivePath(currentPath, path) ? 'active' : ''}`}
              onClick={() => onNavigate(path)}
            >
              <Icon size={15} style={{ flexShrink: 0, color: 'white' }} />
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main Sidebar ──────────────────────────────────────────────────────────────
const Sidebar: React.FC<SidebarProps> = ({ onNavigate, onLogout }) => {
  const { pathname } = useLocation();
  const [expanded, setExpanded] = useState(true);

  const { allowed } = useRoleAccess();

  // Hide items the user can't access; hide groups left with no children.
  // Permission for each path comes from ROUTE_ACCESS via ruleOf().
  const menu: MenuItem[] = visibleMenu
    .map(item =>
      item.type === 'group'
        ? { ...item, children: item.children.filter(c => allowed(ruleOf(c.path))) }
        : item,
    )
    .filter(item =>
      item.type === 'group' ? item.children.length > 0 : allowed(ruleOf(item.path)),
    );

  const itemBtnRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [itemTops, setItemTops] = useState<Record<string, number>>({});
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updatePositions = useCallback(() => {
    const tops: Record<string, number> = {};
    Object.entries(itemBtnRefs.current).forEach(([id, el]) => {
      if (el) tops[id] = el.getBoundingClientRect().top;
    });
    setItemTops(tops);
  }, []);

  useEffect(() => {
    updatePositions();
    window.addEventListener('resize', updatePositions);
    return () => window.removeEventListener('resize', updatePositions);
  }, [expanded, updatePositions]);

  const W = expanded ? 260 : COLLAPSED_W;

  const itemEnter = (key: string) => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setHoveredItem(key);
  };
  const itemLeave = () => {
    hoverTimer.current = setTimeout(() => setHoveredItem(null), 80);
  };

  return (
    <div
      className="sb-root"
      style={{
        width: W, minWidth: W,
        background: SB_BG,
        padding: expanded ? '20px 10px' : '20px 8px',
        color: 'white',
        minHeight: '100vh',
        boxShadow: '2px 0 20px rgba(0,0,0,0.2)',
        display: 'flex', flexDirection: 'column',
        transition: 'width 0.28s cubic-bezier(0.4,0,0.2,1), min-width 0.28s cubic-bezier(0.4,0,0.2,1)',
        overflow: 'visible',
        position: 'relative',
        zIndex: 100,
      }}
    >
      {/* ── Logo row ── */}
      {expanded ? (
        <div style={{ marginBottom: 28, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 2px', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden', minWidth: 0 }}>
            <div style={{ width: 36, height: 36, flexShrink: 0, background: 'rgba(255,255,255,0.18)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IcoGrid size={17} style={{ color: 'white' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <h2 className="sb-label" style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                SkoolPillar
              </h2>
              {isBeta && (
                <span style={{ fontSize: '10px', color: '#a78bfa', fontWeight: 700, letterSpacing: '0.5px' }}>
                  BETA
                </span>
              )}
            </div>
          </div>
          <button className="sb-toggle" onClick={() => setExpanded(p => !p)} title="Collapse sidebar">
            <IcoSidebarClose size={15} style={{ color: 'white' }} />
          </button>
        </div>
      ) : (
        <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 36, height: 36, background: 'rgba(255,255,255,0.18)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
            <IcoGrid size={17} style={{ color: 'white' }} />
            {isBeta && (
              <span style={{
                position: 'absolute', bottom: -3, right: -3,
                width: 8, height: 8, borderRadius: '50%',
                background: '#a78bfa', border: '1.5px solid rgba(30,58,138,0.9)',
              }} />
            )}
          </div>
          <button
            className="sb-toggle"
            onClick={() => setExpanded(p => !p)}
            title="Expand sidebar"
            style={{ width: 36, height: 36, borderRadius: 10 }}
          >
            <IcoSidebarOpen size={15} style={{ color: 'white' }} />
          </button>
          <div style={{ width: 32, height: 1, background: 'rgba(255,255,255,0.15)', margin: '4px 0 2px' }} />
        </div>
      )}

      {/* ── Nav ── */}
      <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'visible', scrollbarWidth: 'none' }} onScroll={updatePositions}>
        {menu.map(item => {

          // ── Group ────────────────────────────────────────────────────────────
          if (item.type === 'group') {
            return (
              <GroupRenderer
                key={item.id}
                item={item}
                expanded={expanded}
                currentPath={pathname}
                onNavigate={onNavigate}
                setExpanded={setExpanded}
                updatePositions={updatePositions}
              />
            );
          }

          // ── Flat item ─────────────────────────────────────────────────────────
          const active = isActivePath(pathname, item.path);

          if (expanded) {
            return (
              <button
                key={item.path}
                className={`sb-btn ${active ? (item.isNew ? 'new-active' : 'active') : ''}`}
                onClick={() => onNavigate(item.path)}
              >
                <item.Icon size={17} style={{ color: 'white', flexShrink: 0 }} />
                <span className="sb-label" style={{ flex: 1 }}>{item.label}</span>
                {item.betaOnly && isBeta && <span className="sb-beta-badge">BETA</span>}
                {item.isNew && !item.betaOnly && <span className="sb-new-badge">NEW</span>}
              </button>
            );
          }

          // ── Flat item collapsed ───────────────────────────────────────────────
          return (
            <div
              key={item.path}
              ref={el => { itemBtnRefs.current[item.path] = el; }}
              onMouseEnter={() => { updatePositions(); itemEnter(item.path); }}
              onMouseLeave={itemLeave}
            >
              <button
                className={`sb-btn ${active ? (item.isNew ? 'new-active' : 'active') : ''}`}
                onClick={() => onNavigate(item.path)}
                style={{ justifyContent: 'center', padding: '11px', position: 'relative' }}
              >
                <item.Icon size={17} style={{ color: 'white', flexShrink: 0 }} />
                {(item.isNew || item.betaOnly) && (
                  <span style={{
                    position: 'absolute', top: 7, right: 7,
                    width: 7, height: 7, borderRadius: '50%',
                    background: item.betaOnly ? '#a78bfa' : '#fbbf24',
                    border: '1.5px solid rgba(30,58,138,0.8)',
                  }} />
                )}
              </button>
              {hoveredItem === item.path && (
                <div className="sb-tip" style={{ top: (itemTops[item.path] ?? 0) + 9 }}>
                  {item.label}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* ── Logout ── */}
      <div style={{ paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
        {expanded ? (
          <button className="sb-btn" onClick={onLogout} style={{ marginBottom: 0 }}>
            <IcoSignOut size={17} style={{ color: 'white', flexShrink: 0 }} />
            <span className="sb-label">Logout</span>
          </button>
        ) : (
          <div
            ref={el => { itemBtnRefs.current['__logout'] = el; }}
            onMouseEnter={() => { updatePositions(); itemEnter('__logout'); }}
            onMouseLeave={itemLeave}
          >
            <button className="sb-btn" onClick={onLogout} style={{ justifyContent: 'center', padding: '11px', marginBottom: 0 }}>
              <IcoSignOut size={17} style={{ color: 'white', flexShrink: 0 }} />
            </button>
            {hoveredItem === '__logout' && (
              <div className="sb-tip" style={{ top: (itemTops['__logout'] ?? 0) + 9 }}>
                Logout
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;